<template>
    <div class="spark-blocks-container">
        <div class="spark-blocks-section-title">
            <span>Contract Block Categories</span>
            <span v-if="allBlocks.length > 0" class="badge bg-light text-muted border fw-normal">
                {{ allBlocks.length }} Library Blocks
            </span>
        </div>

        <!-- Block Grid Tiles -->
        <div class="spark-block-grid">
            <button
                v-for="(button, index) in visibleButtons"
                :key="index"
                type="button"
                class="spark-block-tile"
                :class="{
                    'spark-block-tile--active': selectedBlockLabel === button.label && button.label !== 'Pricing Table',
                    'spark-block-tile--builder': button.label === 'Pricing Table'
                }"
                :disabled="button.disabled || !canEditContract"
                @click="handleClick(button.label)"
                :title="button.label === 'Pricing Table' ? 'Open interactive pricing table builder' : `Browse ${button.label} templates`"
            >
                <span v-if="button.label === 'Pricing Table'" class="spark-block-tile__badge bg-info text-white">
                    Builder
                </span>
                <span v-else-if="getBlockCount(button.label) > 0" class="spark-block-tile__badge bg-light text-muted border">
                    {{ getBlockCount(button.label) }}
                </span>
                <i :class="button.icon" class="spark-block-tile__icon"></i>
                <span class="spark-block-tile__label">{{ button.label }}</span>
            </button>
        </div>

        <!-- Testimonial Variable Field if active -->
        <div v-if="showTestimonial" class="mb-3">
            <testimonial-variable-field></testimonial-variable-field>
        </div>

        <!-- Template Variants Panel -->
        <div v-if="selectedBlockLabel && selectedBlockLabel !== 'Pricing Table' && !showTestimonial" class="spark-variants-panel">
            <div class="spark-variants-header">
                <h6 class="spark-variants-header__title">
                    <i class="fa-solid fa-folder-open text-primary"></i>
                    <span>{{ selectedBlockLabel }} Templates</span>
                    <span class="spark-pill-badge spark-pill-badge--primary ms-1">
                        {{ filteredVariableButtons.length }}
                    </span>
                </h6>
                <input
                    v-if="variableButtons.length > 2"
                    type="text"
                    v-model="searchQuery"
                    placeholder="Filter templates..."
                    class="spark-variant-search-input"
                />
            </div>

            <!-- Variants List -->
            <div v-if="filteredVariableButtons.length > 0" class="spark-variant-list">
                <div v-for="(button, index) in filteredVariableButtons" :key="index" class="spark-variant-card">
                    <div class="spark-variant-card__top">
                        <div class="spark-variant-title">
                            <span class="spark-variant-title__text">{{ button.label }}</span>
                            <span v-if="button.is_default" class="spark-badge-default spark-badge-default--blue">
                                <i class="fa-solid fa-star spark-badge-default__star"></i> Default
                            </span>
                        </div>
                    </div>
                    <div v-if="button.field_value" class="spark-variant-preview">
                        {{ stripHtmlPreview(button.field_value) }}
                    </div>
                    <div class="spark-variant-card__bottom">
                        <div class="spark-variant-actions">
                            <button
                                type="button"
                                class="spark-btn-action-replace"
                                :disabled="!canEditContract"
                                @click="handleReplace(button.section_id, button.field_value)"
                                title="Replace the entire section in contract with this template"
                            >
                                <i class="fa-solid fa-repeat"></i>
                                <span>Replace</span>
                            </button>
                            <button
                                type="button"
                                class="spark-btn-action-append"
                                :disabled="!canEditContract"
                                @click="handleVariableClick(button.section_id, button.field_value)"
                                title="Append this template into the contract section"
                            >
                                <i class="fa-solid fa-plus"></i>
                                <span>Append</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty Variants for this category -->
            <div v-else class="spark-blocks-empty">
                <div class="spark-blocks-empty__icon">
                    <i class="fa-regular fa-folder-open"></i>
                </div>
                <div class="spark-blocks-empty__title">
                    {{ searchQuery ? 'No matching templates' : 'No templates found' }}
                </div>
                <div class="spark-blocks-empty__desc">
                    {{ searchQuery ? `No templates matched "${searchQuery}".` : `No library templates available for ${selectedBlockLabel}.` }}
                </div>
            </div>
        </div>

        <!-- Initial Prompt when no block category is selected yet -->
        <div v-else-if="!showTestimonial" class="spark-blocks-empty border rounded bg-white">
            <div class="spark-blocks-empty__icon text-muted">
                <i class="fa-solid fa-cubes-stacked"></i>
            </div>
            <div class="spark-blocks-empty__title">Select a Block Category</div>
            <div class="spark-blocks-empty__desc">
                Choose a section category above to browse available library templates, or click <strong>Pricing Table</strong> to launch the interactive builder.
            </div>
        </div>

        <Teleport to="body">
            <PricingTableModal ref="pricingTableModal" :setItems="setItems" :isLoading="isLoading"
                @update-pricing-table="updatePricingTable" @save-contract="saveContract" @has-changes="handlePricingChanges"
                @rebuild-schedule="rebuildSchedule" />
        </Teleport>

    <div class="row" style="margin-left: 0px; max-height: 850px; overflow-y: auto;"
        v-if="false">
        <div class="row">
            <button class="col-4 button-select continue" @click.stop="buildPricingTable()"
                :disabled="!hasPricingTableChanges"><i class="fa-solid fa-wrench"></i> &nbsp; Build Table</button>
            <div class="col-4"></div>
            <button class="col-4 button-select continue" @click.stop="addPricingOption()"><i
                    class="fa-solid fa-table"></i> &nbsp; Add Option</button>
        </div>
        <div class="row">
            <br>
        </div>
        <div class="row">
            <table class="product-table">
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Description</th>
                        <th>Notes</th>
                        <th>Quantity</th>
                        <th>Price</th>
                        <th>Discount</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in productList" :key="index">
                        <td class="product-name-cell" :title="item.product"
                            @click.stop="startEdit(index, 'product')">
                            <strong v-if="!item.isEditingProduct">{{ item.product }}</strong>
                            <input v-else v-focus type="text" v-model="item.product" class="product-name-input"
                                @click.stop
                                @blur="endEdit(item, 'product')"
                                @keyup.enter="endEdit(item, 'product')" />
                        </td>
                        <td @click.stop="startEdit(index, 'description')">
                            <span v-if="!item.isEditingDescription">{{ item.description }}</span>
                            <input v-else type="text" v-model="item.description" @blur="endEdit(item, 'description')"
                                @keyup.enter="endEdit(item, 'description')" />
                        </td>
                        <td @click.stop="startEdit(index, 'remarks')">
                            <span v-if="!item.isEditingRemarks">{{ item.remarks }}</span>
                            <input v-else type="text" v-model="item.remarks" @blur="endEdit(item, 'remarks')"
                                maxlength="254" @keyup.enter="endEdit(item, 'remarks')" />
                            <span class="char-counter">
                                {{ (item.remarks || '').length }}/254
                            </span>
                        </td>
                        <td style="text-align: center;" @click.stop="startEdit(index, 'quantity')">
                            <span v-if="!item.isEditingQuantity">{{ item.quantity }}</span>
                            <input v-else type="number" v-model.number="item.quantity" @blur="endEdit(item, 'quantity')"
                                @keyup.enter="endEdit(item, 'quantity')" />
                        </td>
                        <td @click.stop="startEdit(index, 'pricing')">
                            <span v-if="!item.isEditingPricing">{{ formatCurrency(item.pricing) }}</span>
                            <input v-else type="number" v-model.number="item.pricing" @blur="endEdit(item, 'pricing')"
                                @keyup.enter="endEdit(item, 'pricing')" />
                        </td>
                        <td @click.stop="startEdit(index, 'discount')">
                            <span v-if="!item.isEditingDiscount">{{ formatCurrency(item.discount) }}</span>
                            <input v-else type="number" v-model.number="item.discount" @blur="endEdit(item, 'discount')"
                                @keyup.enter="endEdit(item, 'discount')" />
                        </td>
                        <!-- <td>{{ formatCurrency(item.discount) }}</td> -->
                        <td> <button class="button-select remove-button" @click.stop="removeProduct(index)"><i
                                    class="fa-solid fa-trash-can"></i></button></td>
                    </tr>
                    <tr class="add-product-row">
                        <td @click="addProduct(productList.length)">
                            <span>+ Product</span>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr v-if="isSelectingProduct">
                        <td colspan="7">
                            <div style="max-height: 300px; overflow-y: auto;">
                                <table class="product-selection-table">
                                    <tbody>
                                        <tr v-for="(product, index) in availableProducts" :key="index">
                                            <td style="color: #00bcd4"><strong>{{ product.name }}</strong></td>
                                            <!-- <td>{{ product.description ? product.description : "No Product Description" }}
                                        </td> -->
                                            <td>{{ formatCurrency(product.defaultPricing) }}</td>
                                            <td>
                                                <button class="button-select continue"
                                                    @click.stop="selectProduct(product)">Select</button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="pricingOptionCount > 1">
            <div v-for="(productOption, index) in productListOptions" :key="index">
                <span class="orange-label-product-option" style="margin-left: -10px;">Product Pricing for Option {{
                    this.toOptionName(index + 2) }}</span>
                <div class="row">
                    <div class="col-12" style="margin-left: -10px;">
                        <table class="product-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Description</th>
                                    <th>Notes</th>
                                    <th>Quantity</th>
                                    <th>Price</th>
                                    <th>Discount</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in productOption.productList" :key="index">
                                    <td class="product-name-cell" :title="item.product"
                                        @click.stop="startEditOption(productOption.productList, index, 'product')">
                                        <strong v-if="!item.isEditingProduct">{{ item.product }}</strong>
                                        <input v-else v-focus type="text" v-model="item.product" class="product-name-input"
                                            @click.stop
                                            @blur="endEditOption(item, 'product')"
                                            @keyup.enter="endEditOption(item, 'product')" />
                                    </td>
                                    <td style="text-align: center;"
                                        @click.stop="startEditOption(productOption.productList, index, 'description')">
                                        <span v-if="!item.isEditingDescription">{{ item.description }}</span>
                                        <input v-else type="text" v-model="item.description"
                                            @blur="endEditOption(item, 'description')"
                                            @keyup.enter="endEditOption(item, 'description')" />
                                    </td>
                                    <td @click.stop="startEditOption(productOption.productList, index, 'remarks')">
                                        <span v-if="!item.isEditingRemarks">{{ item.remarks }}</span>
                                        <input v-else type="text" v-model="item.remarks"
                                            @blur="endEditOption(item, 'remarks')"
                                            @keyup.enter="endEdit(item, 'remarks')" maxlength="254" />
                                        <span class="char-counter">
                                            {{ (item.remarks || '').length }}/254
                                        </span>
                                    </td>
                                    <td style="text-align: center;"
                                        @click.stop="startEditOption(productOption.productList, index, 'quantity')">
                                        <span v-if="!item.isEditingQuantity">{{ item.quantity }}</span>
                                        <input v-else type="number" v-model.number="item.quantity"
                                            @blur="endEditOption(item, 'quantity')"
                                            @keyup.enter="endEditOption(item, 'quantity')" />
                                    </td>
                                    <td @click.stop="startEditOption(productOption.productList, index, 'pricing')">
                                        <span v-if="!item.isEditingPricing">{{ formatCurrency(item.pricing) }}</span>
                                        <input v-else type="number" v-model.number="item.pricing"
                                            @blur="endEditOption(item, 'pricing')"
                                            @keyup.enter="endEdit(item, 'pricing')" />
                                    </td>
                                    <td @click.stop="startEditOption(productOption.productList, index, 'discount')">
                                        <span v-if="!item.isEditingDiscount">{{ formatCurrency(item.discount) }}</span>
                                        <input v-else type="number" v-model.number="item.discount"
                                            @blur="endEditOption(item, 'discount')"
                                            @keyup.enter="endEdit(item, 'discount')" />
                                    </td>
                                    <!-- <td>{{ formatCurrency(item.discount) }}</td> -->
                                    <td> <button class="button-select remove-button"
                                            @click.stop="removeProductOption(productOption, productOption.productList, index)"><i
                                                class="fa-solid fa-trash-can"></i></button></td>
                                </tr>
                                <tr class="add-product-row">
                                    <td @click="addProductOption(productOption, productOption.productList.length)">
                                        <span>+ Product</span>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td colspan="2" style="text-align: end;" @click="removeOption(productOption)">
                                        <span>Remove Option</span>
                                    </td>
                                </tr>
                                <tr v-if="productOption.isSelectingProduct">
                                    <td colspan="7">
                                        <div style="max-height: 300px; overflow-y: auto;">
                                            <table class="product-selection-table">
                                                <tbody>
                                                    <tr v-for="(product, index) in availableProducts" :key="index">
                                                        <td style="color: #00bcd4"><strong>{{ product.name }}</strong>
                                                        </td>
                                                        <!-- <td>{{ product.description ? product.description : "No Product Description" }}
                                        </td> -->
                                                        <td>{{ formatCurrency(product.defaultPricing) }}</td>
                                                        <td>
                                                            <button class="button-select continue"
                                                                @click.stop="selectProductOption(productOption, product)">Select</button>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
    </div>
</template>

<script>
import 'vue-tel-input/dist/vue-tel-input.css'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import TestimonialVariableField from '@/components/ContentLibrary/TestimonialVariableFields.vue';
import PricingTableModal from './PricingTableModal.vue';
//import ImageGridVariableField from '@/components/ContentLibrary/ImageGridVariableFields.vue';
import { log } from '@/plugins/helper.js'
import { isEmpty } from '../../plugins/helper';


//const _ = require('lodash');

export default {

    components: {
        TestimonialVariableField,
        PricingTableModal,
        //ImageGridVariableField
    },

    directives: {
        focus: {
            mounted(el) {
                el.focus();
            }
        }
    },

    emits: ['onSubmit', 'onDelete', 'onReady', 'pricingTableActive', 'pricingTableInActive', 'update-pricing-table', 'save-contract', 'pricing-table-changes', 'rebuild-schedule'],

    props: {
        item: {
            type: Object,
            // required: true,
        },

        isHidden: {
            type: Boolean,
        },

        setItems: {
            type: Object,
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        contract: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            selectedBlockLabel: '',
            searchQuery: '',
            organisationVariableFields: [],
            organisations: [],
            rolesVariableFields: [],
            productVariableFields: [],
            userVariableFields: [],
            buttons: [
                //{ label: 'Block Details', icon: 'fa-solid fa-circle-info', disabled: false, hidden: false },
                { label: 'Master Terms', icon: 'fa-solid fa-gavel', disabled: false, hidden: false },
                { label: 'Standard', icon: 'fa-solid fa-cube', disabled: false, hidden: false },
                { label: 'Term', icon: 'fa-solid fa-clock', disabled: false, hidden: false },
                { label: 'Subscription', icon: 'fa-solid fa-rotate', disabled: false, hidden: false },
                { label: 'Schedule', icon: 'fa-solid fa-calendar-days', disabled: false, hidden: false },
                { label: 'Execution Page', icon: 'fa-solid fa-clipboard-check', disabled: false, hidden: false },
                // ---- hidden/not applicable to contracts ----
                { label: 'Cover Page', icon: 'fa-solid fa-file-lines', disabled: false, hidden: true },
                { label: 'Product / Service', icon: 'fa-solid fa-box-open', disabled: false, hidden: true },
                { label: 'Pricing Table', icon: 'fa-solid fa-hand-holding-dollar', disabled: false, hidden: false },
                { label: 'Image Grid', icon: 'fa-solid fa-images', disabled: false, hidden: true },
                { label: 'Testimonials', icon: 'fa-solid fa-comments', disabled: false, hidden: true },
                { label: 'Video', icon: 'fa-solid fa-photo-film', disabled: true, hidden: true },
                { label: 'Content', icon: 'fa-solid fa-feather', disabled: false, hidden: true },
                { label: 'Conflict of Interest', icon: 'fa-solid fa-triangle-exclamation', disabled: false, hidden: true },
                { label: 'Insurance', icon: 'fa-solid fa-kit-medical', disabled: false, hidden: true },
                { label: 'WHS Responsibilities', icon: 'fa-solid fa-briefcase', disabled: false, hidden: true },
                { label: 'Terms & Condition', icon: 'fa-solid fa-file-signature', disabled: false, hidden: true },
                { label: 'Acceptance / Execution', icon: 'fa-solid fa-clipboard-check', disabled: false, hidden: true },
                { label: 'Organisation Table', icon: 'fa-solid fa-sitemap', disabled: false, hidden: true },
            ],
            variableButtons: [

            ],
            showTestimonial: false,
            showImageGridSettings: false,
            showPricingTableSettings: false,
            allBlocks: [],
            isSelectingProduct: false,
            pricingOptionCount: 1,
            //product pricing table
            availableProducts: [
                // Sample Data for structure:
                // { name: 'PenTesting Basic', description: 'Basic website penetration testing service', defaultQuantity: 1, defaultDiscount: 0.00, defaultPricing: 100.00 },
            ],
            tempAvailableProducts: [

            ],
            productListOptions: [
            ],
            products: [],
            productList: [
                //Sample Data for structure:
                // { product: 'PenTester', description: 'Pentests websites', quantity: 12, discount: 10.00, pricing: 50.00 },
            ],
            filteredContractItems: [],
            newProductIndex: -1, // To track which row is being edited for adding a product
            isCharityLead: false,
            hasPricingTableChanges: false,
            hasPricingTableActive: false,
            //isLoading: false,
        }
    },

    computed: {
        visibleButtons() {
            return this.buttons.filter(b => !b.hidden);
        },

        filteredVariableButtons() {
            if (!this.searchQuery) return this.variableButtons;
            const q = this.searchQuery.toLowerCase().trim();
            return this.variableButtons.filter(b => 
                (b.label && b.label.toLowerCase().includes(q)) ||
                (b.field_value && b.field_value.toLowerCase().includes(q))
            );
        },

        calculateSubTotalPrice() {
            return this.productList.reduce((total, product) => {
                // Assuming each product has a 'pricing' and 'quantity' property
                const productTotal = (parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0);
                return (total + productTotal);
            }, 0).toFixed(2); // Format to 2 decimal places
        },

        calculateTotalPrice() {
            return this.productList.reduce((total, product) => {
                // Assuming each product has a 'pricing' and 'quantity' property
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return (total + productTotal);
            }, 0).toFixed(2); // Format to 2 decimal places
        },

        calculateTotalGST() {
            return this.productList.reduce((total, product) => {
                // Assuming each product has a 'pricing' and 'quantity' property
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return (total + productTotal);
            }, 0).toFixed(2); // Format to 2 decimal places
        },
        canEditContract() {
            return this.$parent && this.$parent.canEditContract;
        },
    },

    watch: {
        setItems(Val) {
            if (!isEmpty(Val)) {
                this.tempAvailableProducts = JSON.parse(JSON.stringify(this.availableProducts))
                this.productList = []
                this.productListOptions = []
                this.pricingOptionCount = 1
                this.convertLeadProductsToSelectedProducts(Val.items)
                this.availableProducts = JSON.parse(JSON.stringify(this.tempAvailableProducts))
            }
        },
    },

    created() {
        this.init()
    },

    mounted() {
        this.$emit('onReady')
    },

    methods: {

        handlePricingChanges(hasChanges) {
            this.hasPricingTableChanges = hasChanges;
            this.$emit('pricing-table-changes', hasChanges);
        },

        updatePricingTable(html) {
            this.$parent.setSectionContentById("start_pricing_table_section", html);
        },

        saveContract(contract_items) {
            this.$parent.saveContent(contract_items);
        },

        rebuildSchedule(contract_items) {
            this.$emit('rebuild-schedule', contract_items);
        },

        calculateOptionSubtotalPrice(productList) {
            return productList.reduce((total, product) => {
                // Assuming each product has a 'pricing' and 'quantity' property
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0));
                return total + productTotal;
            }, 0).toFixed(2); // Format to 2 decimal places
        },

        calculateOptionTotalPrice(productList) {
            return productList.reduce((total, product) => {
                // Assuming each product has a 'pricing' and 'quantity' property
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 1.1;
                return total + productTotal;
            }, 0).toFixed(2); // Format to 2 decimal places
        },

        calculateOptionGST(productList) {
            return productList.reduce((total, product) => {
                // Assuming each product has a 'pricing' and 'quantity' property
                const productTotal = ((parseFloat(product.pricing) || 0) * (parseInt(product.quantity) || 0) - (product.discount * parseInt(product.quantity) || 0)) * 0.1;
                return total + productTotal;
            }, 0).toFixed(2); // Format to 2 decimal places
        },

        toOptionName(count) {
            // Handle the special case: if count is 1, return an empty string
            if (count <= 1) {
                return '';
            }

            let result = '';
            let num = count; // Start with the actual count for conversion

            while (num > 0) {
                // Calculate the remainder when dividing by 26
                // This gives us the position of the current letter (1 for A, 26 for Z)
                let remainder = num % 26;

                // Adjustment for 'Z' and subsequent columns:
                // If remainder is 0, it means the letter is 'Z' (the 26th letter)
                // and we need to decrement num by 26 to properly carry over for the next iteration.
                if (remainder === 0) {
                    remainder = 26; // Map 0 remainder to the 26th letter ('Z')
                }

                // Convert the remainder to a character
                // 'A' has ASCII value 65. So, (remainder - 1) + 65 gives us the correct character code
                // (e.g., if remainder is 1, 1-1=0, 65+0=65='A')
                result = String.fromCharCode(64 + remainder) + result;

                // Update num for the next iteration
                // If remainder was 26 (meaning 'Z'), we effectively processed a full '26' group.
                // So, subtract 'remainder' and then divide by 26.
                num = Math.floor((num - remainder) / 26);
            }

            return result;
        },

        init() {
            this.getAllVariableFieldBlocks()
            this.getOrganisationVariableFields()
            //this.getProductVariableFields()
            //this.getUserVariableFields()
            this.getAuthOrganisations()
            this.getProductList()
        },

        stripHtmlPreview(html) {
            if (!html) return '';
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const text = tmp.textContent || tmp.innerText || '';
            return text.trim();
        },

        getBlockCount(clickLabel) {
            if (!this.allBlocks || this.allBlocks.length === 0) return 0;
            if (clickLabel === "Master Terms") {
                return this.allBlocks.filter(v => v.BlockType?.name === "MasterTerms" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Standard") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ServiceModuleStandard" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Term") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ServiceModuleTerm" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Subscription") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ServiceModuleSubscription" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Schedule") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ScheduleTable" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Execution Page") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ExecutionPage" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Cover Page") {
                return this.allBlocks.filter(v => v.BlockType?.name === "CoverPage" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Product / Service") {
                return this.allBlocks.filter(v => v.BlockType?.name === "ProductService" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Conflict of Interest") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "ConflictsOfInterest" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Insurance") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "Insurances" && !isEmpty(v.content)).length;
            } else if (clickLabel === "WHS Responsibilities") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "WHSResponsibilities" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Terms & Condition") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Content" && v.content_type?.name === "TermsAndConditions" && !isEmpty(v.content)).length;
            } else if (clickLabel === "Image Grid") {
                return this.allBlocks.filter(v => v.BlockType?.name === "Image" && !isEmpty(v.content)).length;
            }
            return 0;
        },

        handleClick(clickLabel) {
            this.selectedBlockLabel = clickLabel;
            this.searchQuery = '';
            if (clickLabel == "Cover Page") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "CoverPage" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_cover_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Testimonials") {
                this.variableButtons = []
                this.showTestimonial = true
                this.showPricingTableSettings = false
                this.hasPricingTableActive = false
                this.$emit("pricingTableInActive")
                return
            } else if (clickLabel == "Pricing Table") {
                this.$refs.pricingTableModal.openModal();
                return
            } else if (clickLabel == "Product / Service") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ProductService" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_product_services_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Conflict of Interest") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "ConflictsOfInterest" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_conflict_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Insurance") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "Insurances" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_insurance_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "WHS Responsibilities") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "WHSResponsibilities" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_whsr_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Terms & Condition") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Content" && vfield.content_type != undefined && vfield.content_type.name == "TermsAndConditions" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_content_terms_condition_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Image Grid") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "Image" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_image_grid_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Master Terms") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "MasterTerms" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_master_terms_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Standard") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ServiceModuleStandard" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_standard_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Term") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ServiceModuleTerm" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_term_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Subscription") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ServiceModuleSubscription" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_subscription_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            } else if (clickLabel == "Schedule") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ScheduleTable" && !isEmpty(vfield.content)) {
                            const content = typeof vfield.content === 'string'
                                ? vfield.content.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:')
                                : vfield.content;
                            this.variableButtons.push(
                                {
                                    section_id: "start_schedule_table_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: content
                                }
                            )
                        }
                    })
            } else if (clickLabel == "Execution Page") {
                this.variableButtons = []
                if (this.allBlocks.length > 0)
                    this.allBlocks.forEach((vfield) => {
                        if (vfield.BlockType != undefined && vfield.BlockType != null && vfield.BlockType.name == "ExecutionPage" && !isEmpty(vfield.content))
                            this.variableButtons.push(
                                {
                                    section_id: "start_execution_page_section",
                                    label: vfield.block_name,
                                    is_default: vfield.default_version,
                                    field_value: vfield.content
                                }
                            )
                    })
            }

            this.$emit("pricingTableInActive")
            this.hasPricingTableActive = false
            this.showTestimonial = false
            this.showPricingTableSettings = false
        },

        handleVariableClick(section_id, variable_value) {
            // All contract variable replacement is handled by the backend
            // during BuildContractSectionContent — pass raw content through.
            const normalized = typeof variable_value === 'string'
                ? variable_value.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:')
                : variable_value;
            this.$parent.appendToSectionContentById(section_id, normalized);
        },

        async handleReplace(section_id, variable_value) {
            const normalized = typeof variable_value === 'string'
                ? variable_value.replace(/Proposal Link\s*\(test\):/gi, 'Proposal Link:')
                : variable_value;

            const targetContract = this.contract || (this.$parent && (this.$parent.contract || (this.$parent.$refs && this.$parent.$refs.contractDetailsFormRef && this.$parent.$refs.contractDetailsFormRef.contract)));
            if (targetContract && targetContract.ContractSectionsJSONDraft) {
                const sectionKey = section_id.replace(/^start_/, '').replace(/_section$/, '');
                if (typeof targetContract.ContractSectionsJSONDraft === 'object') {
                    targetContract.ContractSectionsJSONDraft[sectionKey] = normalized;
                }
            }

            this.$parent.setSectionContentById(section_id, normalized);
            // Auto-save and resolve variable fields on the backend
            await this.$parent.saveContent([], true);
            await this.$parent.resolveVariableFields();
        },

        GetOrganisationName(org_id) {
            const organisation = this.organisations.find(org => org.id === org_id);
            return organisation ? organisation.name : null; // Or handle the case where no match is found
        },

        addTestimonialToContent(testimonial, b64img) {
            let hasb64Image = b64img != null && b64img != undefined
            let b64NoImage = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAASwAAACoCAMAAABt9SM9AAAAOVBMVEXU1NSampqXl5fX19fMzMyVlZXQ0NCgoKC/v7/ExMSkpKS6urqpqanT09OdnZ3Jycm0tLS1tbWtra0K+9GZAAAC+0lEQVR4nO3ci46iMBSAYeih3OSivv/DLqCFIiACGcl6/i+7yUQHM/wRaIszQQAAAAAAAAAAAAAAAAAAAAAAAAAAAAD8CAniA+TsH/+r5FKZMNz3r/mf1IpyydWGh5j67F34nuhgqzBM9Ly1LuZoLBOfvQ9f08Yqyr2n96iNFZ29D1/Txdp9IMVGUywh1udGsWSzoDsMH1/+Pj/WLd+uPcN3X9yj3881xIqTQ9dFY7Oz9+XP9bGkODyGKM/emb/Wxyq7t8du7cbp2Tvz1/pYUbu/6W7Nyctcfv2sNYplyp27K+1BrC3W7okLsd5vOCojhb7D8NNYUqa3i7+QRazlje62uQBab92PWIvy55DMpi4PsZY2uffDV+vGocRaEA9DfXN1U3BizW+RevOixD1IrPktbv4k0j1IrOUtnOp50iJWa2YlIfNiFZyzvKeLbFqg6lvZjFiD2s4EzNytRlMwzvKeTUJTTbfJbHck2oIR/MuTZnrvR+Jrcyjm3hFKrCDo3kDmNo3Qrjn4jxLLzWuGKeDyy6iPFbkxwvptVPWxJB/Gnu8ytM+pj+XNAU3+5hXSmlji3xLslxemL3CzkfZY5Xi+HJqlk/zdGPWx/FWrR63ZSXaZm5BY07v51XROLXH1vFbqjlVPPvkwHcnL45vUx5LqtdX0JC+pDYn1ssDXG4/km8tgSKzg+ZGamfeWP5K/++N7xbG8m12LJ/nho2/KY03P7q5L/ujRXAa94b3qWN6kcOYkL4FI/Xpwqo3lrnILtS6N0TeojhXMDBu8NM8PRRKrjSXXjZ/E1Rwr2thKc6ztH/HWHOvtGYtY41gJsZa4WEKsdY9YcRzXxFrXrS/YRkisVf7yMbFW9MtSxPqApEl7FBoXqzsmN1EUK+jWEWSY7mz/3V9FsR6O/qITsT6mMdaOY/Dpri5WmOwWqoolKwOEdVbRnzt6t5j80XBr/98d+f80s8QDrCnO3oGvkjI7QMEfDAEAAAAAAAAAAAAAAAAAAAAAAAAAAIAa/wDYuS0/E15ttAAAAABJRU5ErkJggg==`

            if (this.$parent.checkIfIDExist(`testimonial_hash_${testimonial.id}`)) {
                alert("Testimonial already exist")
                return
            }

            this.$parent.addTestimonialToGrid(testimonial, `
            <div class="testimonial-container" id="testimonial_hash_${testimonial.id}">
                 <button class="remove-testimonial-button">X</button>
                <div class="testimonial-avatar">
                    <img src="${(hasb64Image) ? b64img : b64NoImage}" alt="Testimonial Image">
                </div>
                <div class="testimonial-content">
                    <p class="testimonial-text">"${testimonial.testimonial}"</p>
                    <p class="testimonial-author">${testimonial.contact_name}</p>
                    <p class="testimonial-title">${testimonial.contact_role + "-" +
                this.GetOrganisationName(testimonial.organisation_id)}</p>
                </div>
            </div>`)
        },

        //added block type async function
        async getAllVariableFieldBlocks() {
            await this.$axios.get("/blocks/all?blockUse=Contract")
                .then((response) => {
                    this.allBlocks = response.data.blocks
                    log(this.allBlocks)
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        //added block type async function
        async getProductVariableFields() {
            await this.$axios.get("/categories?name=ProductVariableFields")
                .then((response) => {
                    this.productVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        //added block use async function
        async getRolesVariableFields() {
            await this.$axios.get("/categories?name=RolesVariableFields")
                .then((response) => {
                    this.rolesVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        //added block use async function
        async getOrganisationVariableFields() {
            await this.$axios.get("/categories?name=OrganisationVariableFields")
                .then((response) => {
                    this.organisationVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        //added block use async function
        async getUserVariableFields() {
            await this.$axios.get("/categories?name=UserVariableFields")
                .then((response) => {
                    this.userVariableFields = response.data.category.subcategories
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        async getAuthOrganisations() {
            await this.$axios_auth.get("/vertex/organisations")
                .then((response) => {
                    this.organisations = response.data.organisations
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                })
        },

        //pricing table functions
        async getProductList() {
            try {
                const [pricingRes, authRes] = await Promise.allSettled([
                    this.$axios.get("/get-product-pricing"),
                    this.$axios.get("/auth-products")
                ]);

                this.availableProducts = [];

                if (pricingRes.status === "fulfilled" && pricingRes.value?.data?.productPrices) {
                    pricingRes.value.data.productPrices.forEach((p) => {
                        this.availableProducts.push({
                            id: p.ProductID,
                            authItemCode: p.AuthItemCode || "",
                            name: p.DisplayName,
                            defaultPricing: p.Price,
                            price: p.Price,
                            description: p.Description,
                            defaultQuantity: 1,
                            isCharityProduct: p.IsCharityProduct,
                            type: p.Type || "One-Off",
                            planType: p.ContractBasis || p.PricingPlanType || "",
                            forRenewal: p.ForRenewal || false,
                            childProducts: p.ChildProducts || []
                        });
                    });
                }

                if (authRes.status === "fulfilled" && authRes.value?.data?.authProducts) {
                    authRes.value.data.authProducts.forEach((authItem) => {
                        if (!authItem.Product) return;
                        const authCode = authItem.AuthItemCode || authItem.auth_item_code || authItem.product_id || authItem.Product.id || "";
                        const prodId = authItem.product_id || authItem.Product.id || "";
                        const exists = this.availableProducts.some(
                            (item) => (prodId && item.id === prodId) || (authCode && (item.authItemCode === authCode || item.id === authCode))
                        );
                        if (!exists) {
                            this.availableProducts.push({
                                id: prodId,
                                authItemCode: authCode,
                                name: authItem.Product.display_name || authItem.Product.name || "Product",
                                defaultPricing: authItem.Product.price ?? authItem.price ?? 0,
                                price: authItem.Product.price ?? authItem.price ?? 0,
                                description: authItem.Product.description || "",
                                defaultQuantity: 1,
                                isCharityProduct: authItem.is_charity_product === true || authItem.is_charity_product == 1,
                                type: authItem.Product.type || "One-Off",
                                planType: authItem.contract_type || authItem.contract_basis || "",
                                forRenewal: false,
                                childProducts: []
                            });
                        }
                    });
                }
            } catch (error) {
                if (error.response?.data?.message) {
                    this.$toast.error(error.response.data.message);
                }
            }
        },

        formatCurrency(value) {
            const numericValue = parseFloat(value);
            if (isNaN(numericValue)) {
                return '$0.00';
            }
            return `$${numericValue.toFixed(2)}`;
        },
        calculateTotal(item) {
            return (item.quantity * item.pricing) - (item.discount * item.quantity);
        },
        calculateTotalDiscount(item) {
            return (item.discount * item.quantity);
        },
        addProduct(index) {
            this.isSelectingProduct = !this.isSelectingProduct;
            this.newProductIndex = index;
            this.hasPricingTableChanges = true;
        },

        addProductOption(productOption, index) {
            productOption.isSelectingProduct = !productOption.isSelectingProduct;
            productOption.newProductIndex = index;
            this.hasPricingTableChanges = true;
        },

        startEdit(index, field) {
            // Ensure only one field is being edited at a time for a row
            this.productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                    item['current_product'] = item.product
                    item['current_qty'] = item.quantity
                    item['current_price'] = item.pricing
                    item['current_discount'] = item.discount
                    item['current_remarks'] = item.remarks
                    item['current_description'] = item.description
                } else {
                    item.isEditingProduct = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                    item.isEditingRemarks = false;
                    item.isEditingDescription = false;
                }
            });
        },

        //startEdit for options
        startEditOption(productList, index, field) {
            // Ensure only one field is being edited at a time for a row
            productList.forEach((item, i) => {
                if (i === index) {
                    item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = true;
                    item['current_product'] = item.product
                    item['current_qty'] = item.quantity
                    item['current_price'] = item.pricing
                    item['current_discount'] = item.discount
                    item['current_remarks'] = item.remarks
                    item['current_description'] = item.description
                } else {
                    item.isEditingProduct = false;
                    item.isEditingQuantity = false;
                    item.isEditingPricing = false;
                    item.isEditingDiscount = false;
                    item.isEditingRemarks = false;
                    item.isEditingDescription = false;
                }
            });

        },

        endEdit(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            if (item["current_product"] != item["product"] || item["current_qty"] != item["quantity"] || item["current_price"] != item["pricing"] || item["current_discount"] != item["discount"] || item["current_remarks"] != item["remarks"] || item["current_description"] != item["description"]) {
                this.hasPricingTableChanges = true
            }
        },

        endEditOption(item, field) {
            item[`isEditing${field.charAt(0).toUpperCase() + field.slice(1)}`] = false;
            if (item["current_product"] != item["product"] || item["current_qty"] != item["quantity"] || item["current_price"] != item["pricing"] || item["current_discount"] != item["discount"] || item["current_remarks"] != item["remarks"] || item["current_description"] != item["description"]) {
                this.hasPricingTableChanges = true
            }
        },

        selectProduct(selectedProduct) {
            log("product:", selectedProduct)

            const existingProductIndex = this.productList.findIndex(
                (item) => item.id === selectedProduct.id
            );

            const productName = (selectedProduct.name && selectedProduct.name.trim()) ? selectedProduct.name : (selectedProduct.product || "Product");
            const productDesc = (selectedProduct.description !== undefined && selectedProduct.description !== null && String(selectedProduct.description).trim() !== "") ? selectedProduct.description : (selectedProduct.Description || "");

            const newProduct = {
                id: selectedProduct.id,
                product: productName,
                name: productName,
                description: productDesc,
                remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                quantity: selectedProduct.defaultQuantity || selectedProduct.quantity || 1,
                discount: (selectedProduct.isCharityProduct && this.isCharityLead) ? selectedProduct.defaultPricing : (selectedProduct.defaultDiscount || selectedProduct.discount || 0.00),
                pricing: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                price: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                isEditingProduct: false,
                isEditingDescription: false,
                isEditingRemarks: false,
                isEditingQuantity: false,
                isEditingPricing: false,
                isEditingDiscount: false
            };

            if (existingProductIndex > -1) {
                this.productList.splice(existingProductIndex + 1, 0, newProduct);
            } else {
                this.productList.push(newProduct);
            }

            this.isSelectingProduct = false;
            this.newProductIndex = -1;
            this.hasPricingTableChanges = true;
        },

        selectProductOption(productOption, selectedProduct) {
            const existingProductIndex = productOption.productList.findIndex(
                (item) => item.id === selectedProduct.id
            );

            const productName = (selectedProduct.name && selectedProduct.name.trim()) ? selectedProduct.name : (selectedProduct.product || "Product");
            const productDesc = (selectedProduct.description !== undefined && selectedProduct.description !== null && String(selectedProduct.description).trim() !== "") ? selectedProduct.description : (selectedProduct.Description || "");

            const newProduct = {
                id: selectedProduct.id,
                product: productName,
                name: productName,
                description: productDesc,
                remarks: (selectedProduct.remarks) ? selectedProduct.remarks : "",
                quantity: selectedProduct.defaultQuantity || selectedProduct.quantity || 1,
                discount: (selectedProduct.isCharityProduct && this.isCharityLead) ? (selectedProduct.defaultDiscount > 0 ? selectedProduct.defaultDiscount : selectedProduct.defaultPricing) : (selectedProduct.defaultDiscount || selectedProduct.discount || 0.00),
                pricing: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                price: selectedProduct.defaultPricing !== undefined ? selectedProduct.defaultPricing : (selectedProduct.price || 0.00),
                isEditingProduct: false,
                isEditingDescription: false,
                isEditingRemarks: false,
                isEditingQuantity: false,
                isEditingPricing: false,
                isEditingDiscount: false
            };

            if (existingProductIndex > -1) {
                productOption.productList.splice(existingProductIndex + 1, 0, newProduct);
            } else {
                productOption.productList.push(newProduct);
            }

            productOption.isSelectingProduct = false;
            productOption.newProductIndex = -1;
            this.hasPricingTableChanges = true;
        },


        removeProductOption(productOption, productList, indexToRemove) {

            if (productList.length == 1) {
                this.removeOption(productOption)
                return
            }
            // // Find the index of the product to remove
            // const indexToRemove = productList.findIndex(
            //     (item) => item.id === productIdToRemove
            // );

            if (indexToRemove > -1) {
                // Remove the product from the productList array
                productList.splice(indexToRemove, 1);
            }

            this.hasPricingTableChanges = true;
        },

        removeProduct(indexToRemove) {
            // Find the index of the product to remove

            if (this.productList.length == 1) {
                alert("Cannot remove last product on table. Please add another one for replacement.")
                return
            }

            // const indexToRemove = this.productList.findIndex(
            //     (item) => item.id === productIdToRemove
            // );

            if (indexToRemove > -1) {
                // Remove the product from the productList array
                this.productList.splice(indexToRemove, 1);
            }

            this.hasPricingTableChanges = true;
        },

        convertLeadProductsToSelectedProducts(products_array) {
            if (!isEmpty(products_array) && products_array.length > 0) {
                products_array.forEach((p, idx) => {
                    if (p.is_heading || p.isHeading || p.IsHeading) {
                        const headingItem = {
                            id: p.product_id || ("heading-" + Date.now() + "-" + Math.random().toString(36).substr(2, 7)),
                            product: (p.product_name && p.product_name.trim()) ? p.product_name : (p.product || "Section Heading"),
                            name: (p.product_name && p.product_name.trim()) ? p.product_name : (p.product || "Section Heading"),
                            description: p.description || "",
                            remarks: p.remarks || "",
                            quantity: 0,
                            discount: 0,
                            pricing: 0,
                            price: 0,
                            total: 0,
                            is_child: false,
                            is_heading: true,
                            parent_id: null,
                            option_index: p.option_index || 1,
                            line_item_index: p.line_item_index || (idx + 1),
                            isEditingProduct: false,
                            isEditingDescription: false,
                            isEditingRemarks: false,
                            isEditingQuantity: false,
                            isEditingPricing: false,
                            isEditingDiscount: false
                        };
                        if (p.option_index == 1) {
                            this.productList.push(headingItem);
                        } else if (p.option_index >= 2) {
                            while (this.productListOptions.length < p.option_index - 1) {
                                this.productListOptions.push({
                                    productList: [],
                                    isSelectingProduct: false,
                                    newProductIndex: -1
                                });
                                this.pricingOptionCount++;
                            }
                            const option = this.productListOptions[p.option_index - 2];
                            option.productList.push(headingItem);
                        }
                        return;
                    }

                    const productFromList = this.availableProducts.find(
                        (item) => item.id === p.product_id || (item.authItemCode && item.authItemCode === p.product_id)
                    );

                    const resolvedName = (p.product_name && p.product_name.trim())
                        ? p.product_name
                        : (productFromList?.name || p.Product?.display_name || p.Product?.name || p.product || "Product");
                    const resolvedDesc = (p.description !== undefined && p.description !== null && String(p.description).trim() !== "")
                        ? p.description
                        : (productFromList?.description || p.Product?.description || "");
                    const resolvedRemarks = p.remarks || "";
                    const resolvedPrice = p.price !== undefined && p.price !== null
                        ? p.price
                        : (p.pricing !== undefined && p.pricing !== null ? p.pricing : (productFromList?.price !== undefined ? productFromList.price : (productFromList?.defaultPricing || 0)));
                    const resolvedQty = p.quantity !== undefined && p.quantity !== null ? p.quantity : 1;
                    const resolvedDiscount = p.discount !== undefined && p.discount !== null ? p.discount : 0;
                    const resolvedTotal = (resolvedPrice * resolvedQty) - resolvedDiscount;

                    const itemToPush = {
                        id: p.product_id || p.id,
                        name: resolvedName,
                        product: resolvedName,
                        description: resolvedDesc,
                        remarks: resolvedRemarks,
                        price: resolvedPrice,
                        pricing: resolvedPrice,
                        quantity: resolvedQty,
                        discount: resolvedDiscount,
                        total: resolvedTotal,
                        type: productFromList?.type || p.Product?.type || "One-Off",
                        planType: productFromList?.planType || p.Product?.contract_type || p.Product?.contract_basis || "",
                        forRenewal: productFromList?.forRenewal || false,
                        childProducts: productFromList?.childProducts || [],
                        is_child: p.is_child || false,
                        is_heading: false,
                        parent_id: p.parent_id || null,
                        option_index: p.option_index || 1,
                        line_item_index: p.line_item_index || (idx + 1),
                        isEditingProduct: false,
                        isEditingDescription: false,
                        isEditingRemarks: false,
                        isEditingQuantity: false,
                        isEditingPricing: false,
                        isEditingDiscount: false
                    };

                    if (p.is_charity) {
                        this.isCharityLead = true;
                    }

                    if (p.option_index == 1) {
                        this.productList.push(itemToPush);
                    } else if (p.option_index >= 2) {
                        while (this.productListOptions.length < p.option_index - 1) {
                            this.productListOptions.push({
                                productList: [],
                                isSelectingProduct: false,
                                newProductIndex: -1
                            });
                            this.pricingOptionCount++;
                        }
                        const option = this.productListOptions[p.option_index - 2];
                        option.productList.push(itemToPush);
                    }

                    this.hasPricingTableChanges = false;
                });
            }
        },

        addPricingOption() {

            let pO = [];

            if (!isEmpty(this.productList)) {
                this.productList.forEach(p => {
                    pO.push(JSON.parse(JSON.stringify(p)))
                })
            }

            this.productListOptions.push({
                productList: pO,
                isSelectingProduct: false,
                newProductIndex: -1
            })
            this.pricingOptionCount += 1
            this.hasPricingTableChanges = true;
        },

        removeOption(productOption) {
            let index = this.productListOptions.indexOf(productOption)
            this.productListOptions.splice(index, 1)
            this.hasPricingTableChanges = true;
        },

        buildPricingTable() {

    //         let productsToPropagate = ""
    //         let optionsToPropagate = ""

    //         this.productList.forEach(p => {
    //             productsToPropagate +=
    //                 `<tr id='product_list_${p.id}'>
    //                    <td style="border-color: black; border-width: 1px; text-align: center;">${p.product}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.pricing)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.discount)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalDiscount(p))}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotal(p))}</td>
    //                 </tr>
    //             `})

    //         productsToPropagate += `
    //                 <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateSubTotalPrice)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalGST)}</td>
    //   </tr>
    //   <tr>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //        <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total AUD<b></td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalPrice)}</td>
    //   </tr>
    //             `
    //         if (!isEmpty(this.productListOptions))
    //             this.productListOptions.forEach((plo, index) => {
    //                 let optionBody = ""

    //                 if (!isEmpty(plo.productList)) {
    //                     plo.productList.forEach(p => {
    //                         optionBody +=
    //                             `<tr id='product_list_${p.id}'>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${p.product}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.description || "No Product Description"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.remarks || "No Remarks"}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${p.quantity}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.pricing)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(p.discount)}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotalDiscount(p))}</td>
    //       <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateTotal(p))}</td>
    //                 </tr>
    //             `})

    //                     optionBody += `
    //                  <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;"><b>Subtotal<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionSubtotalPrice(plo.productList))}</td>
    //                 </tr>
    //                 <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td title="Goods and Services Tax" style="border-color: black; border-width: 1px; text-align: center;"><b>GST (10%)<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionGST(plo.productList))}</td>
    //                 </tr>
    //                 <tr>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">-</td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;"><b>Total AUD<b></td>
    //                     <td style="border-color: black; border-width: 1px; text-align: center;">${this.formatCurrency(this.calculateOptionTotalPrice(plo.productList))}</td>
    //                 </tr>
    //             `

    //                     optionsToPropagate += `
                
    //             <br>
    //             <span class="orange-label-product-option" style="margin-left: 0;">Pricing for Option ${this.toOptionName(index + 2)}</span>
    //             <table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //             <thead>
    //                 <tr>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Product</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Quantity</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //                     <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total AUD</th>
    //                 </tr>
    //             <thead>
    //             <tbody>
    //                 ${optionBody}
    //             </tbody>
    //             </table>
    //             `
    //                 }
    //             })

    //         this.$parent.setSectionContentById("start_pricing_table_section", `<table style="border-collapse: collapse; width: 100%; border-color: black; border: 2px solid black;" border="1">
    //             <thead>
    //                 <tr>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Product</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Description</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Notes</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Quantity</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Price</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Per Item Discount</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total Discount</th>
    //                      <th style="background-color: #00a0c1; border-color: black; color: white; border-width: 1px; text-align: center;">Total AUD</th>
    //                 </tr>
    //             <thead>
    //             <tbody>
    //                 ${productsToPropagate}
    //             </tbody>
    //             </table>
    //                 ${(!isEmpty(optionsToPropagate) ? optionsToPropagate : "")}
    //         `)

            this.hasPricingTableChanges = false;

            let contract_items = []
            this.productList.forEach((p, idx) => {
                contract_items.push({
                    product_id: p.is_heading ? (p.id ? p.id.toString() : ("heading-" + Date.now())) : p.id.toString(),
                    product_name: p.product,
                    description: p.description || "",
                    price: p.is_heading ? 0 : p.pricing,
                    quantity: p.is_heading ? 0 : p.quantity,
                    discount: p.is_heading ? 0 : p.discount,
                    is_heading: p.is_heading || false,
                    line_item_index: p.line_item_index || (idx + 1),
                    option_index: 1,
                    remarks: p.remarks || ""
                })
            })

            if (!isEmpty(this.productListOptions))
                this.productListOptions.forEach((plo, index) => {
                    if (!isEmpty(plo.productList)) {
                        plo.productList.forEach((p, optIdx) => {
                            contract_items.push({
                                product_id: p.is_heading ? (p.id ? p.id.toString() : ("heading-" + Date.now())) : p.id.toString(),
                                product_name: p.product,
                                description: p.description || "",
                                price: p.is_heading ? 0 : p.pricing,
                                quantity: p.is_heading ? 0 : p.quantity,
                                discount: p.is_heading ? 0 : p.discount,
                                is_heading: p.is_heading || false,
                                line_item_index: p.line_item_index || (optIdx + 1),
                                option_index: index + 2,
                                remarks: p.remarks || ""
                            })
                        })
                    }
                })

            log(contract_items)
            this.$parent.saveContent(contract_items)
            // this.filterContractItems(contract_items)
        },
    },
};

</script>

<style scoped>
/* form start */
form label:not(#isCharityLbl) {
    color: #00A0C1;
    margin: 0.5rem 0;
}

input:not([type="checkbox"]),
select {
    height: 40px;
    border-radius: 10px;
    max-width: 100px;
    border: 1px solid #D9D9D9;
    color: #435666;
}

.form--check-input {
    width: 12px;
    /* Increase width */
    height: 12px;
    /* Increase height */
    margin-left: -17px;
    margin-right: 10px;
}

/* form end */
.btn {
    width: 7rem;
}

.add-space {
    margin-bottom: 225px;
}

.button-group {
    display: grid;
    /* Use grid for layout */
    grid-template-columns: repeat(4, 1fr);
    /* 4 equal-width columns */
    grid-gap: 10px;
    /* Spacing between grid items */
}

.button-group-variable {
    display: grid;
    /* Use grid for easy layout */
    grid-template-columns: repeat(1, 1fr);
    /* 4 columns */
    grid-gap: 5px;
    /* Spacing between buttons */
    padding: 10px;
    overflow-y: auto;
    scrollbar-color: #00A0C1 lightblue;
    max-height: clamp(300px, calc(100vh - 300px), 900px);
    overflow-x: hidden;
}

.block-button-container {
    display: flex;
    margin-left: -3px;
    gap: 3px;
}

@media (min-width: 1900px) {

    /* Adjust breakpoint as needed */
    .block-button-container {
        gap: 6px;
        /* Larger gap for larger screens */
    }
}



/* pricing table */

.product-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    border: 1px solid #ddd;
}

.product-table th,
.product-table td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

.product-table th {
    background-color: #00bcd4;
    color: white;
    font-weight: bold;
}

.product-table tr:last-child td {
    border-bottom: none;
}

.product-table td:first-child {
    align-items: center;
}

.product-table td:first-child>span {
    margin-right: 5px;
}

.add-product-row td {
    color: #00bcd4;
    cursor: pointer;
}

.remove-button {
    background-color: #ff9800;
    color: whitesmoke;
    border: 1px solid #ff9800;
}

.add-product-row td:first-child {
    font-weight: bold;
}

.add-product-row td:last-child {
    font-weight: bold;
    color: #ff8c00
}

.product-selection-list {
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    padding: 10px;
    margin-top: 5px;
    border-radius: 5px;
}

.product-item {
    padding: 8px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.product-item:hover {
    background-color: #eee;
}

.product-table td input[type="number"] {
    width: 70px;
    /* Adjust this value to control the width */
    padding: 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9em;
    text-align: left;
    /* Align the number to the right */
}

.product-table td input[type="number"]:focus {
    outline: none;
    border-color: #00bcd4;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.5);
}

.product-selection-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
}

.product-selection-table th,
.product-selection-table td {
    padding: 8px;
    border: 1px solid #ddd;
    text-align: left;
}

.product-selection-table th {
    background-color: #f2f2f2;
    font-weight: bold;
    /* Optional: Keep header fixed during scroll */
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #f2f2f2;
}

.product-item {
    /* Remove default styling for the div */
    padding: 0;
    margin: 0;
    border: none;
    cursor: pointer;
    /* Optional: indicate it's interactive */
}

.product-item strong {
    /* Style the product name */
}

.total-row {
    font-weight: bold;
    background-color: #f9f9f9;
}

.total-label {
    text-align: right;
    padding-right: 8px;
}

.total-amount {
    text-align: right;
}

.continue {
    background-color: #00bcd4;
    color: white;
}

.continue:disabled {
    background-color: lightgray;
    color: white;
}

.actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.button-select {
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
}

.orange-label-product-option {
    display: inline-block;
    /* Allows padding and margin to work like a block */
    background-color: #ff8c00;
    /* A vibrant orange */
    color: white;
    /* White text for contrast */
    padding: 6px 12px;
    /* Good spacing around the text */
    border-radius: 5px;
    /* Slightly rounded corners */
    font-weight: bold;
    /* Bold text */
    font-size: 0.95em;
    /* Slightly smaller than default text */
    text-transform: uppercase;
    /* All caps for a label look */
    letter-spacing: 0.5px;
    /* A little space between letters */
}

.char-counter {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 2px;
}

.product-name-cell {
    cursor: pointer;
}

.product-name-input {
    width: 100%;
    min-width: 120px;
    padding: 4px 8px;
    border: 1px solid #00bcd4;
    border-radius: 4px;
    font-size: 0.9em;
    font-weight: bold;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 188, 212, 0.4);
}
</style>
